import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronRight, ChevronsDownUp, ChevronsUpDown } from 'lucide-react'

/** Optional page-level expand/collapse-all broadcast. A parent bumps `epoch`
 *  (with the desired `open`) and every CollapsiblePanel under the provider
 *  snaps to that state — while individual header toggles keep working between
 *  broadcasts. No provider → panels are purely self-managed. */
export type BulkCollapse = { epoch: number; open: boolean }
export const CollapsibleBulkContext = createContext<BulkCollapse | null>(null)

/** A Panel whose body collapses behind a clickable header. Same card styling as
 *  Panel/PanelHeader; the header is a toggle (chevron rotates), the body hides
 *  when closed. Opt-in — plain Panel stays non-collapsible.
 *
 *  `actions` sit beside the toggle, never inside it: a link or button nested in
 *  the header button is invalid HTML and its click would toggle the panel. */
export function CollapsiblePanel({
  title,
  subtitle,
  meta,
  actions,
  defaultOpen = true,
  onOpenChange,
  children,
  className = '',
}: {
  title: ReactNode
  /** A second line under the title, still part of the toggle. */
  subtitle?: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  defaultOpen?: boolean
  /** Called with each new state, from a header click or a bulk broadcast. */
  onOpenChange?: (open: boolean) => void
  children: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(defaultOpen)
  const bulk = useContext(CollapsibleBulkContext)
  const lastEpoch = useRef(bulk?.epoch)
  // The latest callback, so a caller's inline arrow does not re-run the effect.
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  // Apply an expand/collapse-all broadcast (only when epoch actually changes,
  // so the initial mount keeps each panel's own defaultOpen).
  useEffect(() => {
    if (bulk && bulk.epoch !== lastEpoch.current) {
      lastEpoch.current = bulk.epoch
      setOpen(bulk.open)
      onOpenChangeRef.current?.(bulk.open)
    }
  }, [bulk?.epoch, bulk?.open, bulk])
  const toggle = () => {
    setOpen(!open)
    onOpenChange?.(!open)
  }
  return (
    <div className={`rounded-lg border border-line bg-surface ${className}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className={`flex min-w-0 flex-1 items-center justify-between gap-3 py-3 pl-4 text-left ${actions ? 'pr-1' : 'pr-4'}`}
        >
          <span className="flex min-w-0 items-center gap-2">
            <ChevronRight
              className={'h-4 w-4 shrink-0 text-ink-mut transition-transform ' + (open ? 'rotate-90' : '')}
              aria-hidden="true"
            />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-ink">{title}</span>
              {subtitle ? <span className="block text-xs text-ink-soft">{subtitle}</span> : null}
            </span>
          </span>
          {meta ? (
            <span className="flex shrink-0 items-center gap-2 text-sm text-ink-soft">{meta}</span>
          ) : null}
        </button>
        {actions ? <div className="flex shrink-0 items-center gap-2 pr-4">{actions}</div> : null}
      </div>
      {open ? <div className="border-t border-line">{children}</div> : null}
    </div>
  )
}

const BULK_BUTTON = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-ink-soft hover:bg-canvas'

/** The Expand all / Collapse all pair that drives a CollapsibleBulkContext. */
export function ExpandCollapseAll({
  onExpandAll,
  onCollapseAll,
  className = '',
}: {
  onExpandAll: () => void
  onCollapseAll: () => void
  className?: string
}) {
  return (
    <div className={`flex items-center overflow-hidden rounded-md border border-line ${className}`}>
      <button type="button" onClick={onExpandAll} className={BULK_BUTTON}>
        <ChevronsUpDown className="h-3.5 w-3.5" aria-hidden="true" />
        Expand all
      </button>
      <button type="button" onClick={onCollapseAll} className={`${BULK_BUTTON} border-l border-line`}>
        <ChevronsDownUp className="h-3.5 w-3.5" aria-hidden="true" />
        Collapse all
      </button>
    </div>
  )
}
